import React from 'react';
import {
  Typography,
  Stack,
  useMediaQuery,
} from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { StoryTypeSelect } from '../components/StoryTypeSelect';
import { StoryTagAutocomplete } from '../components/StoryTagAutocomplete';
import { StatusSelect } from '../components/StatusSelect';


export type Filters = keyof Stories.ListParams;

export interface Props {
  filters?: Stories.GetListParams;
  setters?: {
    [K in Filters]?: (value: Stories.GetListParams[K]) => void;
  };
  disabledFilters?: Filters[];
}

type StoriesFilterProps = Props & {
  storiesMetadata: CVT.Query.Metadata | null;
}

export const StoriesFilters: React.FC<StoriesFilterProps> = ({
  filters,
  setters,
  disabledFilters = [],
  storiesMetadata,
}) => {
  const dictionary = useDictionary();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Stack p={2} pb={0} gap={1}>
      <Stack direction="row">
        <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap={{ xs: 'nowrap', sm: 'wrap' }} alignItems="center" gap={2} width="100%">
          {!disabledFilters.includes('type') && (
            <StoryTypeSelect
              value={filters?.type || ''}
              fullWidth={isMobile}
              size="small"
              sx={{
                minWidth: 250,
                width: isMobile ? '100%' : 250,
              }}
              label=""
              placeholder={dictionary.forms.story.allStoryTypes}
              onChange={(event) => {
                const selectedValue = event.target.value as Stories.StoryType;
                setters?.type?.(selectedValue);
              }}
            />
          )}

          {!disabledFilters.includes('tags') && (
            <StoryTagAutocomplete
              sx={{ minWidth: 250, width: isMobile ? '100%' : 250 }}
              size="small"
              TextFieldProps={{
                placeholder: dictionary.forms.story.tagsFilterPlaceholder,
              }}
              label=""
              value={filters?.tags || []}
              onChange={(value) => setters?.tags?.(value)}
            />
          )}

          <StatusSelect
            fullWidth={isMobile}
            size="small"
            value={filters?.status || ''}
            onChange={(event) => setters?.status?.(event.target.value || undefined)}
          />
        </Stack>
      </Stack>
      <Typography variant="caption" color="textSecondary">
        {getMetadataTitle(storiesMetadata)}
      </Typography>
    </Stack>
  );
};

const getMetadataTitle = (metadata: CVT.Query.Metadata | null) => {
  if (!metadata) {
    return 'Loading...';
  }

  return metadata.absoluteTotal === 0
    ? 'No stories available yet'
    : `${metadata.count} stories (${metadata.absoluteTotal} total)`;
};
